import { Alert, Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import { getEnumLabel } from "../SLN/market-growth/constants/commissionEnums";
import {
  PARTNER_LEVEL_LABELS,
  PARTNER_STATUS_LABELS,
  PARTNER_TYPE_LABELS,
  canShowPartnerJoinLink,
  getPartnerStatusChipColor,
  isPartnerActive,
} from "../SLN/market-growth/constants/partnerEnums";
import CopyTextField from "./components/CopyTextField";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";

const Field = ({ label, value }) => (
  <>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5 }}>
      {value ?? "—"}
    </Typography>
  </>
);

const PartnerProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const res = await PartnerCommissionApi.getProfile();
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری پروفایل ناموفق بود."
      );
      if (normalized.noPartnerProfile) {
        setNoPartnerProfile(true);
        setData(null);
        return;
      }
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
        return;
      }
      setData(normalized.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PartnerCommissionPageShell
      title="پروفایل نمایندگی"
      description="اطلاعات حساب نمایندگی شما."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={load}
    >
      {!isPartnerActive(data?.status) || data?.hasStatusWarning ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data?.statusWarningMessage ||
            "حساب نمایندگی شما در وضعیت فعال نیست. برخی امکانات ممکن است محدود باشد."}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                label="سطح نماینده"
                value={getEnumLabel(PARTNER_LEVEL_LABELS, data?.partnerLevel)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="نوع نماینده"
                value={getEnumLabel(PARTNER_TYPE_LABELS, data?.partnerType)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                وضعیت
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  size="small"
                  label={getEnumLabel(PARTNER_STATUS_LABELS, data?.status)}
                  color={getPartnerStatusChipColor(data?.status)}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label="گروه پورسانتی" value={data?.commissionGroupTitle} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label="کد معرف" value={data?.referralCode} />
            </Grid>
            {String(data?.partnerLevel || "").toLowerCase() === "level2" ? (
              <Grid item xs={12} sm={6}>
                <Field
                  label="مدیر فروش"
                  value={data?.parentPartnerDisplayName ?? "—"}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
              <Field
                label="اجازه ساخت زیرمجموعه"
                value={data?.canCreateSubPartners ? "بله" : "خیر"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="تاریخ ایجاد"
                value={
                  data?.createdAt
                    ? new Date(data.createdAt).toLocaleDateString("fa-IR")
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CopyTextField
                label="لینک معرف (مشتری)"
                value={data?.referralLink}
                copyMessage="لینک معرف کپی شد."
              />
            </Grid>
            {(canShowPartnerJoinLink(data) || data?.partnerJoinLink) && data?.partnerJoinLink ? (
              <Grid item xs={12}>
                <CopyTextField
                  label="لینک ثبت‌نام زیرمجموعه"
                  value={data.partnerJoinLink}
                  copyMessage="لینک ثبت‌نام زیرمجموعه کپی شد."
                />
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
    </PartnerCommissionPageShell>
  );
};

export default PartnerProfilePage;
